import Take3D from "../models/Take3D";
import { IAttach } from "../models/interactions/Attach";
import { IDetach } from "../models/interactions/Detach";
import { InteractionType } from "../models/interactions/Interaction";
import { ISequence } from "../models/interactions/SequenceModel";

export type BubbleTargetContext =
  | { kind: "timeline"; interactionId: string }
  | { kind: "sequence"; nodeId: string; sequence?: ISequence };

const applyAttachmentModifier = (
  attached: boolean,
  propId: string,
  interaction: { type: InteractionType },
): boolean => {
  if (
    interaction.type === InteractionType.Attach &&
    (interaction as IAttach).propGuid === propId
  ) {
    return true;
  }

  if (
    interaction.type === InteractionType.Detach &&
    (interaction as IDetach).propGuid === propId
  ) {
    return false;
  }

  return attached;
};

const getSequenceModifierPropIds = (sequence: ISequence): Set<string> => {
  const propIds = new Set<string>();
  (sequence.nodes ?? []).forEach((node) => {
    const interaction = node.data.interaction;
    if (
      interaction.type === InteractionType.Attach ||
      interaction.type === InteractionType.Detach
    ) {
      const propId = (interaction as IAttach | IDetach).propGuid;
      if (propId) propIds.add(propId);
    }
  });
  return propIds;
};

const getSequenceEntryStates = (
  sequence: ISequence,
  propId: string,
  initiallyAttached: boolean,
  rootNodeIds: string[],
): Map<string, Set<boolean>> => {
  const nodes = sequence?.nodes ?? [];
  const edges = sequence?.edges ?? [];
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const outgoingByNodeId = new Map<string, string[]>();
  edges.forEach((edge) => {
    const targets = outgoingByNodeId.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoingByNodeId.set(edge.source, targets);
  });
  const statesByNode = new Map<string, Set<boolean>>();
  const queue: Array<{ nodeId: string; attached: boolean }> = [];

  const enqueue = (nodeId: string, attached: boolean) => {
    if (!nodesById.has(nodeId)) return;
    const states = statesByNode.get(nodeId) ?? new Set<boolean>();
    if (states.has(attached)) return;
    states.add(attached);
    statesByNode.set(nodeId, states);
    queue.push({ nodeId, attached });
  };

  new Set(rootNodeIds).forEach((rootId) => enqueue(rootId, initiallyAttached));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const node = nodesById.get(current.nodeId);
    if (!node) continue;

    const attachedAfterNode = applyAttachmentModifier(
      current.attached,
      propId,
      node.data.interaction,
    );

    (outgoingByNodeId.get(node.id) ?? []).forEach((targetId) =>
      enqueue(targetId, attachedAfterNode),
    );
  }

  return statesByNode;
};

type AttachmentEffect = "none" | "attach" | "detach";

const getAttachmentEffect = (
  propId: string,
  interaction: { type: InteractionType },
): AttachmentEffect => {
  if (
    interaction.type === InteractionType.Attach &&
    (interaction as IAttach).propGuid === propId
  ) {
    return "attach";
  }

  if (
    interaction.type === InteractionType.Detach &&
    (interaction as IDetach).propGuid === propId
  ) {
    return "detach";
  }

  return "none";
};

const getSequenceBranchTerminalEffects = (
  sequence: ISequence,
  startNodeId: string,
  propId: string,
  initialEffect: AttachmentEffect,
): Set<AttachmentEffect> => {
  const nodesById = new Map(
    (sequence.nodes ?? []).map((node) => [node.id, node]),
  );
  const outgoingByNodeId = new Map<string, string[]>();
  (sequence.edges ?? []).forEach((edge) => {
    const targets = outgoingByNodeId.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoingByNodeId.set(edge.source, targets);
  });
  const visited = new Map<string, Set<AttachmentEffect>>();
  const terminalEffects = new Set<AttachmentEffect>();
  const queue: Array<{ nodeId: string; effect: AttachmentEffect }> = [];

  const enqueue = (nodeId: string, effect: AttachmentEffect) => {
    if (!nodesById.has(nodeId)) return;
    const effects = visited.get(nodeId) ?? new Set<AttachmentEffect>();
    if (effects.has(effect)) return;
    effects.add(effect);
    visited.set(nodeId, effects);
    queue.push({ nodeId, effect });
  };

  enqueue(startNodeId, initialEffect);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const node = nodesById.get(current.nodeId);
    if (!node) continue;

    const modifier = getAttachmentEffect(propId, node.data.interaction);
    const effectAfterNode = modifier === "none" ? current.effect : modifier;
    const targets = outgoingByNodeId.get(node.id) ?? [];

    if (targets.length === 0) {
      terminalEffects.add(effectAfterNode);
      continue;
    }

    targets.forEach((targetId) => enqueue(targetId, effectAfterNode));
  }

  return terminalEffects;
};

const combineParallelBranchEffects = (
  branchEffects: Set<AttachmentEffect>[],
): Set<AttachmentEffect> => {
  if (branchEffects.some((effects) => effects.size === 0)) return new Set();

  const combinedEffects = new Set<AttachmentEffect>();
  let everyBranchCanLeaveStateUnchanged = branchEffects.length > 0;

  branchEffects.forEach((effects) => {
    everyBranchCanLeaveStateUnchanged &&= effects.has("none");
    if (effects.has("attach")) combinedEffects.add("attach");
    if (effects.has("detach")) combinedEffects.add("detach");
  });

  if (everyBranchCanLeaveStateUnchanged) combinedEffects.add("none");
  return combinedEffects;
};

const getSequenceTerminalStates = (
  sequence: ISequence,
  propId: string,
  initiallyAttached: boolean,
): Set<boolean> => {
  const nodes = sequence.nodes ?? [];
  const sequenceRoots = nodes.filter(
    (node) => node.data.interaction.type === InteractionType.Sequence,
  );
  const triggerRoots = nodes.filter(
    (node) => node.data.interaction.type === InteractionType.Trigger,
  );
  const roots = sequenceRoots.length > 0 ? sequenceRoots : triggerRoots;
  const outgoingByNodeId = new Map<string, string[]>();
  (sequence.edges ?? []).forEach((edge) => {
    const targets = outgoingByNodeId.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoingByNodeId.set(edge.source, targets);
  });
  const terminalEffects = new Set<AttachmentEffect>();

  roots.forEach((root) => {
    const rootModifier = getAttachmentEffect(propId, root.data.interaction);
    const targets = outgoingByNodeId.get(root.id) ?? [];
    if (targets.length === 0) {
      terminalEffects.add(rootModifier);
      return;
    }

    const branchEffects = targets.map((targetId) =>
      getSequenceBranchTerminalEffects(
        sequence,
        targetId,
        propId,
        rootModifier,
      ),
    );
    const rootInteraction = root.data.interaction as Partial<ISequence>;
    const finishAll = rootInteraction.finishAll ?? sequence.finishAll ?? true;
    const rootEffects =
      root.data.interaction.type === InteractionType.Sequence && finishAll
        ? combineParallelBranchEffects(branchEffects)
        : new Set(branchEffects.flatMap((effects) => [...effects]));

    rootEffects.forEach((effect) => terminalEffects.add(effect));
  });

  const terminalStates = new Set<boolean>();
  terminalEffects.forEach((effect) => {
    if (effect === "attach") terminalStates.add(true);
    else if (effect === "detach") terminalStates.add(false);
    else terminalStates.add(initiallyAttached);
  });

  return terminalStates;
};

const applyCompletedSequence = (
  attachedPropIds: Set<string>,
  sequence: ISequence,
) => {
  const candidateIds = new Set([
    ...attachedPropIds,
    ...getSequenceModifierPropIds(sequence),
  ]);

  candidateIds.forEach((propId) => {
    const terminalStates = getSequenceTerminalStates(
      sequence,
      propId,
      attachedPropIds.has(propId),
    );

    if (terminalStates.size === 0) return;
    if (!terminalStates.has(false)) attachedPropIds.add(propId);
    else attachedPropIds.delete(propId);
  });
};

const getTimelineAttachedPropIds = (
  take: Take3D,
  interactionId: string,
): Set<string> => {
  const attachedPropIds = new Set(Object.keys(take.attachments ?? {}));
  const targetIndex = (take.interactions ?? []).findIndex(
    (interaction) => interaction.id === interactionId,
  );

  if (targetIndex < 0) return attachedPropIds;

  for (const interaction of take.interactions.slice(0, targetIndex)) {
    if (interaction.type === InteractionType.Attach) {
      attachedPropIds.add((interaction as IAttach).propGuid);
    } else if (interaction.type === InteractionType.Detach) {
      attachedPropIds.delete((interaction as IDetach).propGuid);
    } else if (interaction.type === InteractionType.Sequence) {
      applyCompletedSequence(attachedPropIds, interaction as ISequence);
    }
  }

  return attachedPropIds;
};

const isPropAttachedOnEverySequencePath = (
  sequence: ISequence,
  targetNodeId: string,
  propId: string,
  initiallyAttached: boolean,
): boolean => {
  const nodes = sequence?.nodes ?? [];
  const edges = sequence?.edges ?? [];
  const targetNode = nodes.find((node) => node.id === targetNodeId);
  if (!targetNode) return initiallyAttached;

  const roots = nodes.filter(
    (node) =>
      node.data.interaction.type === InteractionType.Sequence ||
      node.data.interaction.type === InteractionType.Trigger,
  );
  if (!edges.some((edge) => edge.target === targetNodeId)) {
    roots.push(targetNode);
  }
  const statesByNode = getSequenceEntryStates(
    sequence,
    propId,
    initiallyAttached,
    roots.map((root) => root.id),
  );

  const targetStates = statesByNode.get(targetNodeId);
  return Boolean(targetStates?.size) && !targetStates!.has(false);
};

const getSequenceAttachedPropIds = (
  take: Take3D,
  sequence: ISequence,
  nodeId: string,
): Set<string> => {
  const initiallyAttachedPropIds = getTimelineAttachedPropIds(
    take,
    sequence.id,
  );
  const candidateIds = new Set(initiallyAttachedPropIds);

  getSequenceModifierPropIds(sequence).forEach((propId) =>
    candidateIds.add(propId),
  );

  return new Set(
    [...candidateIds].filter((propId) =>
      isPropAttachedOnEverySequencePath(
        sequence,
        nodeId,
        propId,
        initiallyAttachedPropIds.has(propId),
      ),
    ),
  );
};

export const getBubbleTargetPropIds = (
  take: Take3D,
  context: BubbleTargetContext,
): Set<string> => {
  const targetPropIds = new Set(
    (take.props ?? []).map((propState) => propState.instanceId),
  );
  const attachedPropIds =
    context.kind === "timeline"
      ? getTimelineAttachedPropIds(take, context.interactionId)
      : getSequenceAttachedPropIds(
          take,
          context.sequence ?? take.sequence,
          context.nodeId,
        );

  attachedPropIds.forEach((propId) => targetPropIds.add(propId));
  return targetPropIds;
};
